import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Util from '../../util/AspectRatioCardUtil';
import './AspectRatioCard.less';

class AspectRatioCard extends Component {
    static propTypes = {
        /** The card aspect ratio, which determines the maintained aspect ratio of the image block. */
        aspectRatio: PropTypes.oneOf([
            '16x9',
            '3x2',
            '4x3',
            '1x1',
            '3x4'
        ]).isRequired,
        /** Flag to indicate whether to use the default hover state of the aspect ratio card */
        hasHover: PropTypes.bool,
        /** Flag to indicate whether to use the darkening/brightening overlay effect with the hover state. Only applies if hasHover and card implements onLoad callback. In general, should be used, unless card figure is not an image. */
        hasHoverOverlay: PropTypes.bool
    };

    static defaultProps = {
        hasHover: true,
        hasHoverOverlay: true
    };

    /**
     * Method intended for use with the getControlOffset property of the
     * CardCarousel to center the controls within the "card" slot instead of
     * centering over the full height which is the default behavior.
     *
     * @param {Number} cardWidth - Width of the card in the CardCarousel in pixels.
     * @param {Number} controlHeight - Height in pixels of the CardCarousel control.
     * @param {String} aspectRatio - The aspect ratio of the card.
     */
    static getControlOffset(cardWidth, controlHeight, aspectRatio) {
        const aspectRatioData = Util.getAspectRatioData(aspectRatio);
        const imageHeight = Math.round(cardWidth * aspectRatioData.heightMultiplier);

        return Math.round(imageHeight ? (imageHeight - controlHeight) / 2 : 0);
    }

    state = {
        loaded: false
    };

    handleLoad = (cb) => {
        return (event) => {
            if (!this.state.loaded) {
                this.setState({
                    loaded: true
                });
            }

            if (cb) {
                cb(event);
            }
        };
    }

    /**
     * Filter the children to determine which slots each child should be assigned
     * during render.
     *
     * @param {Object} children - The opaque children of the component.
     */
    getSlots(
        children,
        slots = {
            figure: null,
            caption: null,
            content: null,
            extra: []
        }
    ) {
        const shouldHandleLoad = this.props.hasHover && this.props.hasHoverOverlay;
        /* eslint-disable complexity */
        React.Children.forEach(children, (child) => {
            const childIsValidElement = React.isValidElement(child);
            const childSlot = childIsValidElement && child.props && child.props['data-slot'];
            if (childSlot && childSlot in slots && !slots[childSlot]) {
                const props = {};
                let cloneChild = false;
                // Pass through the aspect ratio property for
                // AspectRatioCardImage children.
                if (child.type && child.type.displayName && child.type.displayName === 'AspectRatioCardImage') {
                    cloneChild = true;
                    props.aspectRatio = this.props.aspectRatio;
                }
                // Use the "card" slot onLoad method for async loading determination.
                if (childSlot === 'figure' && shouldHandleLoad && !this.state.loaded && child.props.onLoad) {
                    cloneChild = true;
                    props.onLoad = this.handleLoad(child.props.onLoad);
                }
                if (cloneChild) {
                    child = React.cloneElement(child, props);
                }
                slots[childSlot] = child;
            } else if (childIsValidElement && child.type === React.Fragment && 'children' in child.props) {
                this.getSlots(child.props.children, slots);
            } else {
                slots.extra.push(child);
            }
        });
        /* eslint-enable complexity */

        return slots;
    }

    render() {
        const {
            aspectRatio,
            hasHover,
            hasHoverOverlay,
            children
        } = this.props;
        const useHasHoverOverlayModifier = this.state.loaded && hasHover && hasHoverOverlay;
        const aspectRatioData = Util.getAspectRatioData(aspectRatio);
        const rootClass = classnames('AspectRatioCard', {
            'AspectRatioCard--has-hover': hasHover,
            'AspectRatioCard--has-hover-overlay': useHasHoverOverlayModifier
        });
        const topClass = classnames('AspectRatioCard__top', `AspectRatioCard__top--${aspectRatioData.ratioString}`);
        const bottomClass = classnames('AspectRatioCard__bottom', 'text-default');
        const figureClass = classnames('AspectRatioCard__figure', {
            'AspectRatioCard__figure--has-hover-overlay': useHasHoverOverlayModifier
        });
        const captionClass = classnames('AspectRatioCard__caption');
        const contentClass = classnames('AspectRatioCard__content');
        const slots = this.getSlots(children);
        const figureElement = hasHover ?
            (
                <div className="AspectRatioCard__figure-perspective-wrapper">
                    <div className={figureClass}>
                        {slots.figure}
                    </div>
                </div>
            ) :
            (
                <div className={figureClass}>
                    {slots.figure}
                </div>
            );

        return (
            <div className={rootClass}>
                {slots.figure &&
                    <div className={topClass}>
                        {figureElement}
                    </div>
                }
                {(slots.caption || slots.content) &&
                    <div className={bottomClass}>
                        {slots.caption &&
                            <div className={captionClass}>
                                {slots.caption}
                            </div>
                        }
                        {slots.content &&
                            <div className={contentClass}>
                                {slots.content}
                            </div>
                        }
                    </div>
                }
            </div>
        );
    }
}

export default AspectRatioCard;
